import _defineProperty from "@babel/runtime/helpers/defineProperty";
import OriginTracer from '@atlassiansox/origin-tracing';
import sortBy from 'lodash/sortBy';
import { v4 as uuid } from 'uuid';
import { DEFAULT_SLACK_SERVICE_URL } from './constants';
import popupWindowParams from './popupWindowParams';
import { isAcceptableFailure } from './share-to-slack/utils';
import { failUfoExperience, startUfoExperience, succeedUfoExperience, UfoExperiences } from './ufo-experiences';
import { buildQueryString, fetchItemsWithCursor, httpRetryPolicy, retryFetch } from './util';
const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 900;
const USER_FETCH_LIMIT = 1000;
const retryOptions = {
  maxAttempts: 3,
  backoffTimeMs: 200,
  rateLimitBackoffTimeMs: 1000
};
export default class AbstractSlackService {
  constructor(cloudId, product, analytics) {
    _defineProperty(this, "windowName", uuid());
    _defineProperty(this, "abortController", typeof AbortController !== 'undefined' ? new AbortController() : null);
    _defineProperty(this, "userStream", {
      count: 0,
      pages: 0,
      finishedLoading: false
    });
    _defineProperty(this, "channelStream", {
      count: 0,
      pages: 0,
      finishedLoading: false
    });
    // Handle Slack authorization success message from popup window
    _defineProperty(this, "onMessage", ({
      data,
      target,
      source
    }) => {
      var _this$onSuccess;
      if (!source || target !== window || data !== 'success') {
        return;
      }
      window.removeEventListener('message', this.onMessage);
      const popupWindow = source;
      if (!this.popupWindow) {
        return;
      }
      popupWindow.close();
      this.popupWindow = undefined;
      (_this$onSuccess = this.onSuccess) === null || _this$onSuccess === void 0 ? void 0 : _this$onSuccess.call(this);
    });
    this.cloudId = cloudId;
    this.product = product;
    this.analytics = analytics;
    this.baseUrl = `${DEFAULT_SLACK_SERVICE_URL}/${product}`;
  }
  abort() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    }
  }
  async getConsent(onSuccess) {
    await this.primeConsentUrl();
    this.onSuccess = onSuccess;
    window.addEventListener('message', this.onMessage);
    const popupWindow = window.open(this.consentUrl, this.windowName, popupWindowParams(POPUP_WIDTH, POPUP_HEIGHT));
    if (popupWindow) {
      this.popupWindow = popupWindow;
    }
  }
  async isSlackEnabled() {
    const result = await retryFetch({
      request: () => {
        var _this$abortController;
        return fetch(`${this.baseUrl}/enabled?cloudId=${this.cloudId}`, {
          signal: (_this$abortController = this.abortController) === null || _this$abortController === void 0 ? void 0 : _this$abortController.signal
        });
      },
      mapResult: response => response.json().then(({
        enabled
      }) => enabled),
      mapError: response => response.json().then(obj => {
        const {
          code,
          message
        } = obj;
        return {
          code: code !== null && code !== void 0 ? code : '',
          message: message !== null && message !== void 0 ? message : ''
        };
      })
    });
    if (result.ok) {
      this.analytics.onGetEnabledSucceeded();
    } else if (!result.aborted && !isAcceptableFailure(result.status)) {
      this.analytics.onGetEnabledFailed({
        status: result.status,
        errorMessage: result.message
      });
    }
    return result;
  }
  async getTeams() {
    var _this$abortController2;
    const response = await fetchItemsWithCursor({
      url: `${this.baseUrl}/teams`,
      queryParams: {
        cloudId: this.cloudId
      },
      signal: (_this$abortController2 = this.abortController) === null || _this$abortController2 === void 0 ? void 0 : _this$abortController2.signal,
      getItems: response => response.teams,
      getCursor: response => response.nextCursor,
      canRetryCheck: httpRetryPolicy(retryOptions)
    });
    if (response.ok) {
      this.analytics.onFetchTeamsSucceeded({
        teams: response.items.length,
        pages: response.pages
      });
      return {
        ok: true,
        result: sortBy(response.items, [({
          name
        }) => name.toLowerCase()])
      };
    } else if (response.aborted) {
      return response;
    } else {
      this.analytics.onFetchTeamsFailed({
        page: response.page,
        status: response.status,
        errorMessage: response.message
      });
      return response;
    }
  }
  streamUsers(teamId, onResponse, cursor) {
    if (!cursor) {
      this.userStream = {
        count: 0,
        pages: 0,
        finishedLoading: false
      };
    }
    retryFetch({
      request: () => {
        var _this$abortController3;
        return fetch(`${this.baseUrl}/recipients${buildQueryString({
          cloudId: this.cloudId,
          teamId,
          cursor,
          limit: USER_FETCH_LIMIT
        })}`, {
          signal: (_this$abortController3 = this.abortController) === null || _this$abortController3 === void 0 ? void 0 : _this$abortController3.signal
        });
      },
      canRetryCheck: httpRetryPolicy(retryOptions)
    }).then(resp => {
      if (!resp.ok) {
        if (!resp.aborted) {
          this.analytics.onFetchUsersFailed({
            teamId,
            page: this.userStream.pages,
            status: resp.status,
            errorMessage: resp.message
          });
        }
        onResponse(resp);
        return;
      }
      this.userStream.pages++;
      this.userStream.count += resp.result.recipients.length;
      this.userStream.finishedLoading = !!resp.result.nextCursor;
      onResponse({
        ok: true,
        result: resp.result.recipients,
        nextCursor: resp.result.nextCursor
      });
      if (resp.result.nextCursor) {
        this.streamUsers(teamId, onResponse, resp.result.nextCursor);
      } else {
        // This event may not consistently fire because it is possible for the user to select and share before
        // all pages have loaded
        this.analytics.onFetchUsersSucceeded({
          teamId,
          users: this.userStream.count,
          pages: this.userStream.pages
        });
      }
    });
  }
  streamChannels(teamId, onResponse, cursor) {
    if (!cursor) {
      this.channelStream = {
        count: 0,
        pages: 0,
        finishedLoading: false
      };
    }
    retryFetch({
      request: () => {
        var _this$abortController4;
        return fetch(`${this.baseUrl}/channels${buildQueryString({
          cloudId: this.cloudId,
          teamId,
          cursor
        })}`, {
          signal: (_this$abortController4 = this.abortController) === null || _this$abortController4 === void 0 ? void 0 : _this$abortController4.signal
        });
      },
      canRetryCheck: httpRetryPolicy(retryOptions)
    }).then(resp => {
      if (!resp.ok) {
        if (!resp.aborted) {
          this.analytics.onFetchChannelsFailed({
            teamId,
            page: this.channelStream.pages,
            status: resp.status,
            errorMessage: resp.message
          });
        }
        onResponse(resp);
        return;
      }
      this.channelStream.pages++;
      this.channelStream.count += resp.result.channels.length;
      this.channelStream.finishedLoading = !!resp.result.nextCursor;
      onResponse({
        ok: true,
        result: resp.result.channels,
        nextCursor: resp.result.nextCursor
      });
      if (resp.result.nextCursor) {
        this.streamChannels(teamId, onResponse, resp.result.nextCursor);
      } else {
        // This event may not consistently fire because it is possible for the user to select and share before
        // all pages have loaded
        this.analytics.onFetchChannelsSucceeded({
          teamId,
          channels: this.channelStream.count,
          pages: this.channelStream.pages
        });
      }
    });
  }
  async fetchSlackUsers(teamId) {
    var _this$abortController5;
    const response = await fetchItemsWithCursor({
      url: `${this.baseUrl}/recipients`,
      queryParams: {
        cloudId: this.cloudId,
        teamId
      },
      signal: (_this$abortController5 = this.abortController) === null || _this$abortController5 === void 0 ? void 0 : _this$abortController5.signal,
      getItems: response => response.recipients,
      getCursor: response => response.nextCursor,
      canRetryCheck: httpRetryPolicy(retryOptions)
    });
    if (response.ok) {
      const users = sortBy(response.items, [({
        name
      }) => name.toLowerCase()]);
      this.analytics.onFetchUsersSucceeded({
        teamId,
        users: response.items.length,
        pages: response.pages
      });
      return {
        ok: true,
        result: users
      };
    } else if (response.aborted) {
      return response;
    } else {
      this.analytics.onFetchUsersFailed({
        teamId,
        page: response.page,
        status: response.status,
        errorMessage: response.message
      });
      return response;
    }
  }
  async share({
    teamId,
    conversationId,
    conversationType,
    link,
    message
  }) {
    var _this$abortController7;
    startUfoExperience(UfoExperiences.SLACK_REQUEST_SENT);
    const origin = new OriginTracer({
      product: this.product
    });
    const response = await retryFetch({
      request: () => {
        var _this$abortController6;
        return fetch(`${this.baseUrl}/share`, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            cloudId: this.cloudId,
            teamId,
            conversation: {
              id: conversationId,
              type: conversationType
            },
            link: origin.addToUrl(link),
            message
          }),
          signal: (_this$abortController6 = this.abortController) === null || _this$abortController6 === void 0 ? void 0 : _this$abortController6.signal
        });
      },
      mapResult: () => Promise.resolve(),
      mapError: response => response.json().then(obj => {
        const {
          code,
          message
        } = obj;
        return {
          code: code !== null && code !== void 0 ? code : '',
          message: message !== null && message !== void 0 ? message : ''
        };
      }),
      signal: (_this$abortController7 = this.abortController) === null || _this$abortController7 === void 0 ? void 0 : _this$abortController7.signal
    });
    if (response.ok) {
      const successAttributes = {
        product: this.product,
        teamId,
        conversationType,
        conversationId,
        messageLength: message.length || 0,
        userCount: this.userStream.count
      };
      this.analytics.onShareSucceeded(successAttributes);
      succeedUfoExperience(UfoExperiences.SLACK_REQUEST_SENT, successAttributes);
    } else if (!response.aborted && !isAcceptableFailure(response.status)) {
      const failedAttributes = {
        product: this.product,
        teamId,
        conversationType,
        conversationId,
        status: response.status,
        errorMessage: response.message
      };
      this.analytics.onShareFailed(failedAttributes);
      failUfoExperience(UfoExperiences.SLACK_REQUEST_SENT, failedAttributes);
    }
    return response.ok ? {
      ok: true,
      result: origin
    } : response;
  }
}